import React, { useState } from "react";
import { FaFolder, FaFolderOpen, FaChevronRight, FaChevronDown } from "react-icons/fa";

const FolderTree = ({ folders, selectedFolder, setSelectedFolder, parentId = null, level = 0, onDeleteFolder, files = [] }) => {
  const [expanded, setExpanded] = useState({});
  let children = folders.filter(f => f.parentId === parentId);
  // Move selected folder to top
  if (selectedFolder) {
    const selectedIdx = children.findIndex(f => f.id === selectedFolder);
    if (selectedIdx > -1) {
      const [selected] = children.splice(selectedIdx, 1);
      children = [selected, ...children];
    }
  }

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <ul className={`pl-${level * 4} transition-all duration-300`}> {/* Indent based on level */}
      {children.map(folder => (
        <li key={folder.id} className="mb-1">
          <div
            className={`flex items-center cursor-pointer rounded-lg px-2 py-1 transition-colors duration-200 group ${selectedFolder === folder.id ? 'bg-blue-100 shadow-md' : 'hover:bg-gray-100'}`}
            onClick={() => {
              if (selectedFolder === folder.id) {
                setSelectedFolder(null);
              } else {
                setSelectedFolder(folder.id);
              }
            }}
          >
            <span
              onClick={e => { e.stopPropagation(); toggleExpand(folder.id); }}
              className="transition-transform duration-300 flex items-center"
              style={{ transform: expanded[folder.id] ? 'rotate(90deg)' : 'rotate(0deg)' }}
            >
              {expanded[folder.id] ? <FaChevronDown /> : <FaChevronRight />}
            </span>
            <span className="mx-2">
              {expanded[folder.id] ? <FaFolderOpen className="text-yellow-500 transition-transform duration-300 scale-110" /> : <FaFolder className="text-yellow-500 transition-transform duration-300" />}
            </span>
            <div className="flex flex-1 items-center min-w-0">
              <span className="font-medium group-hover:text-blue-700 transition-colors duration-200 truncate" style={{ flex: '1 1 0%', minWidth: 0 }} title={folder.name}>{folder.name}</span>
              <span className="ml-3 text-gray-400 text-xs whitespace-nowrap" style={{ flexShrink: 0 }}>
                <span className="block sm:hidden">{formatDateOnly(folder.updatedAt || folder.modifiedAt || folder.createdAt)}</span>
                <span className="hidden sm:block">{formatDate(folder.updatedAt || folder.modifiedAt || folder.createdAt)}</span>
              </span>
            </div>
            {/* Folder delete button (show on hover) */}
            {onDeleteFolder && (
              <button
                className={`ml-auto text-red-500 hover:text-red-700 px-2 py-1 rounded transition-opacity duration-200 ${selectedFolder === folder.id ? 'opacity-100' : 'opacity-60 hover:opacity-100'}`}
                title="Delete this folder and all its contents"
                onClick={e => { e.stopPropagation(); onDeleteFolder(folder); }}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M6 6l12 12M6 18L18 6"/></svg>
              </button>
            )}
            {/* Forward/Replace icon for future move/rename actions */}
            {/* <span className="ml-auto text-gray-400 hover:text-blue-500 cursor-pointer transition-colors duration-200"><FaExchangeAlt /></span> */}
          </div>
          <div
            style={{
              maxHeight: expanded[folder.id] ? 500 : 0,
              overflow: 'hidden',
              transition: 'max-height 0.4s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            {expanded[folder.id] && (
              <>
                {/* Render subfolders */}
                <FolderTree
                  folders={folders}
                  selectedFolder={selectedFolder}
                  setSelectedFolder={setSelectedFolder}
                  parentId={folder.id}
                  level={level + 1}
                  onDeleteFolder={onDeleteFolder}
                  files={files}
                />
                {/* Render files in this folder */}
                {files && files.filter(f => f.folderId === folder.id).map(file => (
                  <div
                    key={file.id}
                    className="flex items-center ml-8 px-2 py-1 text-gray-700 bg-gray-50 rounded mb-1 text-xs"
                    style={{ marginLeft: `${(level + 1) * 16}px` }}
                  >
                    <span className="mr-2">📄</span>
                    <span className="truncate font-medium">{file.name}</span>
                    <span className="ml-2 text-gray-400">{formatSize(file.size)}</span>
                    <span className="ml-2 text-gray-400">
                      <span className="block sm:hidden">{formatDateOnly(file.updatedAt || file.modifiedAt || file.createdAt)}</span>
                      <span className="hidden sm:block">{formatDate(file.updatedAt || file.modifiedAt || file.createdAt)}</span>
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};


function formatSize(bytes) {
  if (bytes == null || isNaN(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes < 1024 * 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  return `${(bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`;
}


function formatDate(date) {
  if (!date) return "";
  let d = date;
  if (typeof d === "object" && d && typeof d.toDate === "function") {
    d = d.toDate();
  } else if (typeof d === "string" || typeof d === "number") {
    d = new Date(d);
  }
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function formatDateOnly(date) {
  if (!date) return "";
  let d = date;
  if (typeof d === "object" && d && typeof d.toDate === "function") {
    d = d.toDate();
  } else if (typeof d === "string" || typeof d === "number") {
    d = new Date(d);
  }
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

export default FolderTree;
